import {
  Deferred,
  MethodInfo,
  NextUnaryFn,
  RpcError,
  RpcInterceptor,
  RpcMetadata,
  RpcOptions,
  RpcStatus,
  UnaryCall,
} from '@protobuf-ts/runtime-rpc'

export class RetryInterceptor implements RpcInterceptor {
  private maxRetries: number
  private retryDelay: number

  constructor(maxRetries: number = 5, retryDelay: number = 1000) {
    this.maxRetries = maxRetries
    this.retryDelay = retryDelay
  }

  interceptUnary(
    next: NextUnaryFn,
    method: MethodInfo,
    input: object,
    options: RpcOptions,
  ): UnaryCall {
    const defHeader = new Deferred<RpcMetadata>()
    const defMessage = new Deferred<object>()
    const defStatus = new Deferred<RpcStatus>()
    const defTrailer = new Deferred<RpcMetadata>()

    let retries = 0
    let result = next(method, input, options)

    void (async () => {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        try {
          await result
          defHeader.resolve(result.headers)
          defMessage.resolve(result.response)
          defStatus.resolve(result.status)
          defTrailer.resolve(result.trailers)

          break
        } catch (err) {
          if (
            err instanceof RpcError &&
            err.code === 'UNAVAILABLE' &&
            ++retries <= this.maxRetries
          ) {
            console.warn(
              `gRPC connection unavailable, retrying after ${this.retryDelay / 1000}s... (Retry ${retries} of ${this.maxRetries})`,
            )

            await this.sleep()

            result = next(method, input, options)

            continue
          }

          defHeader.rejectPending(err)
          defMessage.rejectPending(err)
          defStatus.rejectPending(err)
          defTrailer.rejectPending(err)
          break
        }
      }
    })()

    return new UnaryCall(
      method,
      options.meta ?? {},
      input,
      defHeader.promise,
      defMessage.promise,
      defStatus.promise,
      defTrailer.promise,
    )
  }

  sleep(): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, this.retryDelay))
  }
}
